import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { instalarCapturaPrecoce } from './app/core/services/telemetry/sentry/sentry-buffer';
import { instalarRetryDeChunk } from './app/core/services/telemetry/chunk-retry';
import { armarVigiaDeBoot } from './app/core/services/telemetry/boot-watchdog';
import { CLIENT_CODE_VERSION } from './app/core/services/telemetry/order-flow-trace.service';
import { sondarCacheDoSw } from './app/core/services/telemetry/sw-cache-probe';

// PRIMEIRA linha executável do bundle, de propósito. É a marca que a sonda de
// boot morto (inline no index.html) procura: sem ela, o `main.js` não executou,
// e o cliente está olhando o splash estático que nunca vai ser substituído.
//
// Tem de ser a primeira porque qualquer coisa acima pode lançar — e um bundle
// que começa a rodar e morre na linha 2 é um caso diferente de um bundle que
// nunca chegou. Só este ponto separa os dois. Ver o bloco no fim do index.html.
(window as any).__mpJsVivo = 1;

// Captura precoce (~1 KB, sem dependência): enfileira erro de window/promise
// desde ANTES do bootstrap. O SDK do Sentry entra por chunk lazy minutos depois
// (fora do caminho crítico) e faz o replay desta fila — sem isto, "lazy"
// significaria não ver nenhum erro de boot, que é justamente onde moram os
// piores. Só a fila é eager; nada aqui importa o SDK. Ver sentry-buffer.ts.
instalarCapturaPrecoce();

// Retentativa de chunk lazy. Tem de entrar ANTES do bootstrap porque o primeiro
// chunk pedido é o da rota inicial — instalar depois deixaria de fora justamente
// o carregamento mais frágil. Medido em 12/08/2026: o ChunkLoadError de
// `/payment` é 99,6% iOS pedindo chunks que EXISTEM na borda (825 devices em 2
// dias, 1 Android) — requisição morta em trânsito no WKWebView, para a qual a
// cura é pedir de novo, não recarregar a página de quem acabou de pagar.
// Devolve `false` sem alterar nada se o runtime do webpack não estiver ao
// alcance; ver chunk-retry.ts.
instalarRetryDeChunk();

// Vigia de boot. Precisa ser armado ANTES do `bootstrapModule` porque o que ele
// mede é o bootstrap não acontecer — o APP_INITIALIZER do cardápio segura o
// Angular, e enquanto ele não volta o usuário olha o esqueleto do index.html
// ("Carregando o cardápio…"). Nenhum serviço injetável existiria nesse cenário.
//
// Medido em 15/08/2026: os erros que sabemos medir explicam ~1/3 das sessões
// iOS que nunca veem o cardápio. O resto trava em silêncio. Ver boot-watchdog.ts.
if (typeof window !== 'undefined') {
  armarVigiaDeBoot({
    url: environment.orderFlowLogURL,
    clientCodeVersion: CLIENT_CODE_VERSION,
  });
}

// Sonda do cache do PWA: descobre QUAL service worker está instalado neste
// device, lendo o nome do cache (o worker antigo não responderia a um
// `postMessage`). Fire-and-forget — o boot não espera, e o campo fica ausente
// se o erro chegar antes da sonda. É a régua que faltava para contar quantos
// devices seguem com o worker anterior à Etapa 1. Ver sw-cache-probe.ts.
sondarCacheDoSw();

// Swiper (web component que substitui o ion-slides, removido no Ionic 7) NÃO é
// mais registrado aqui (Lever 1): saía ~370KB no vendor eager de toda visita.
// Agora é lazy via ensureSwiperRegistered(), só nas telas com carrossel de
// destaques (header). Ver src/app/shared/utils/lazy-swiper.ts.

if (environment.production) {
  enableProdMode();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    (window as any).__PWA_DEFERRED_PROMPT__ = e;
  });
}

platformBrowserDynamic().bootstrapModule(AppModule, {
  // Coalescing (Caso 15): eventos/microtasks no mesmo frame disparavam vários
  // ciclos de CD durante o boot do cardápio — colapsa para um por frame.
  ngZoneEventCoalescing: true,
  ngZoneRunCoalescing: true,
})
  .catch(err => console.error(err));
